import { doc, getDoc, setDoc, collection, getDocs, addDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const pointsPerQuizAnswer = 50;
const maxPoints = 1000;

// Fonction pour calculer la progression basée sur le nombre total de points
export const calculateProgress = (competenceProgress, competence) => {
  if (!competenceProgress) {
    console.warn(`Aucune donnée trouvée pour la compétence : ${competence}. Retour à 0% de progression.`);
    return 0;
  }

  const { totalCorrectAnswers = 0, Points_Exercices = 0 } = competenceProgress;
  const quizPoints = totalCorrectAnswers * pointsPerQuizAnswer;
  const totalPoints = quizPoints + Points_Exercices;
  const progressPercentage = Math.min((totalPoints / maxPoints) * 100, 100);

  console.log(`Calcul de la progression pour ${competence} :`);
  console.log(`  - Points Quiz : ${quizPoints}`);
  console.log(`  - Points Exercices : ${Points_Exercices}`);
  console.log(`  - Total Points : ${totalPoints}`);
  console.log(`  - Progression calculée : ${progressPercentage}%`);

  return Math.round(progressPercentage);
};

// Fonction pour déterminer le niveau de compétence en fonction de la progression
export const getLevel = (progress) => {
  console.log("Détermination du niveau de compétence...");
  if (progress <= 25) return 'Débutant';
  if (progress <= 50) return 'En progrès';
  if (progress <= 75) return 'Avancé';
  return 'Maitrisé';
};

// Initialisation de la progression pour une compétence spécifique
export const initializeCompetenceProgress = (competenceId) => {
  console.log("Initialisation de la progression pour une compétence...");
  const initialProgress = {
    quizCompleted: 0,
    exercisesCompleted: 0,
    Points_Exercices: 0,
    totalCorrectAnswers: 0,
    totalQuestionsAnswered: 0,
    progress: 0,
    courseListened: false,
  };
  console.log(`Initialisation de la progression pour ${competenceId} :`, JSON.stringify(initialProgress));
  return initialProgress;
};

// Charger la progression d'un utilisateur pour une compétence spécifique
export const loadCompetenceProgress = async (userId, competenceId) => {
  console.log(`Chargement de la progression pour la compétence ${competenceId}...`);

  if (!userId || typeof userId !== 'string') {
    console.error("userId est indéfini ou non valide (pas une chaîne). Impossible de charger la progression.");
    return null;
  }

  const competenceProgressRef = doc(db, "users", userId, "competenceProgress", competenceId);
  try {
    const competenceProgressSnap = await getDoc(competenceProgressRef);
    
    if (competenceProgressSnap.exists()) {
      return competenceProgressSnap.data();
    } else {
      // Initialiser la progression si elle n'existe pas
      const initialProgress = initializeCompetenceProgress(competenceId);
      await setDoc(competenceProgressRef, initialProgress);
      return initialProgress;
    }
  } catch (error) {
    console.error(`Erreur lors de la récupération de la progression pour ${competenceId}:`, error.message);
    throw new Error(`Erreur lors de la récupération des données de progression pour ${competenceId}.`);
  }
};

// Charger la progression d'un utilisateur pour toutes ses compétences
export const loadUserProgress = async (userId) => {
  console.log("Chargement de la progression utilisateur... UserID reçu:", userId, "Type:", typeof userId);

  if (!userId || typeof userId !== 'string') {
    console.error("userId est indéfini ou non valide (pas une chaîne). Impossible de charger la progression de l'utilisateur.");
    return {};
  }

  // Vérifier l'état d'authentification
  const auth = getAuth();
  console.log("État authentification:", auth.currentUser ? "Connecté" : "Non connecté");
  if (auth.currentUser) {
    console.log("UID dans auth:", auth.currentUser.uid);
    console.log("UID passé en paramètre:", userId);
    console.log("Les UIDs correspondent:", auth.currentUser.uid === userId);
  }

  try {
    // Récupérer toutes les compétences disponibles pour cet utilisateur
    const competencesCollection = collection(db, "users", userId, "competences");
    const competencesSnapshot = await getDocs(competencesCollection);
    const competenceData = competencesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Récupérer toutes les progressions existantes pour l'utilisateur
    const progressCollection = collection(db, "users", userId, "competenceProgress");
    const progressSnapshot = await getDocs(progressCollection);
    
    // Créer un objet pour stocker toutes les progressions
    const competenceProgress = {};
    
    // Ajouter les progressions existantes
    progressSnapshot.docs.forEach(doc => {
      competenceProgress[doc.id] = doc.data();
    });
    
    // Initialiser les progressions manquantes
    for (const { id: competenceId } of competenceData) {
      if (!competenceProgress[competenceId]) {
        const initialProgress = initializeCompetenceProgress(competenceId);
        try {
          await setDoc(doc(db, "users", userId, "competenceProgress", competenceId), initialProgress);
          competenceProgress[competenceId] = initialProgress;
        } catch (error) {
          console.error(`Erreur lors de l'initialisation de la progression pour ${competenceId}:`, error.message);
          // Continuer malgré l'erreur pour les autres compétences
        }
      }
    }

    return competenceProgress;
  } catch (error) {
    console.error("Erreur lors de la récupération de la progression:", error.message);
    throw new Error("Erreur lors de la récupération des données de progression.");
  }
};

// Fonction pour sauvegarder la progression dans Firestore
export const saveProgressToFirestore = async (userId, competenceId, updatedProgress) => {
  console.log("Début de la sauvegarde de la progression dans Firestore... paramètres :", { userId, competenceId, updatedProgress });

  if (!userId || typeof userId !== 'string') {
    console.error("Erreur : userId est indéfini ou n'est pas une chaîne.");
    return;
  }

  if (!competenceId || typeof competenceId !== 'string') {
    console.error("Erreur : competenceId est indéfini ou n'est pas une chaîne.");
    return;
  }

  const competenceProgressRef = doc(db, "users", userId, "competenceProgress", competenceId);
  try {
    const currentProgressSnap = await getDoc(competenceProgressRef);
    
    if (currentProgressSnap.exists()) {
      // Mettre à jour la progression existante
      await updateDoc(competenceProgressRef, updatedProgress);
    } else {
      // Créer une nouvelle progression
      const initialProgress = initializeCompetenceProgress(competenceId);
      await setDoc(competenceProgressRef, {
        ...initialProgress,
        ...updatedProgress
      });
    }

    console.log(`Progression sauvegardée avec succès pour la compétence ${competenceId}.`);
  } catch (error) {
    console.error("Erreur lors de la sauvegarde:", error.message);
    throw new Error("Erreur lors de la sauvegarde de la progression.");
  }
};

// Mise à jour de la progression après une réponse au quiz
export const updateQuizProgress = async (user, competenceId, totalCorrectAnswers, totalQuestionsAnswered) => {
  // Assurer la compatibilité avec l'ancien code qui utilisait 'user' comme paramètre
  const userId = user;
  console.log("Mise à jour de la progression après une réponse au quiz...");

  try {
    // Récupérer la progression actuelle
    const competenceProgress = await loadCompetenceProgress(userId, competenceId);
    
    // Mettre à jour les données de quiz
    const updatedProgress = {
      totalCorrectAnswers,
      totalQuestionsAnswered,
    };
    
    // Calculer la nouvelle progression
    const newProgress = calculateProgress({
      ...competenceProgress,
      ...updatedProgress
    }, competenceId);
    
    console.log(`Nouveau calcul de progression pour ${competenceId}: ${newProgress}%`);
    
    // Sauvegarder la progression mise à jour
    await saveProgressToFirestore(userId, competenceId, { 
      ...updatedProgress, 
      progress: newProgress 
    });
    
    console.log(`Progression mise à jour pour ${competenceId}: ${newProgress} (Total Correct Answers: ${totalCorrectAnswers}, Total Questions Answered: ${totalQuestionsAnswered})`);
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la progression du quiz:", error.message);
    throw error;
  }
};

// Mise à jour de la progression après la complétion d'un exercice
export const updateExerciseProgress = async (user, competenceId, exercisesCompleted, pointsEarned) => {
  // Assurer la compatibilité avec l'ancien code qui utilisait 'user' comme paramètre
  const userId = user;
  try {
    // Récupérer la progression actuelle
    const competenceProgress = await loadCompetenceProgress(userId, competenceId);
    const currentPoints = competenceProgress?.Points_Exercices || 0;
    
    // Mettre à jour les données d'exercice
    const updatedProgress = {
      exercisesCompleted,
      Points_Exercices: currentPoints + pointsEarned,
    };
    
    console.log(`Points avant mise à jour pour ${competenceId} : ${currentPoints}`);
    console.log(`Points ajoutés pour l'exercice : ${pointsEarned}`);
    
    // Calculer la nouvelle progression
    const newProgress = calculateProgress({
      ...competenceProgress,
      ...updatedProgress
    }, competenceId);
    
    // Sauvegarder la progression mise à jour
    await saveProgressToFirestore(userId, competenceId, { 
      ...updatedProgress, 
      progress: newProgress 
    });
    
    console.log(`Exercices terminés pour ${competenceId}: progression mise à jour avec ${updatedProgress.Points_Exercices} points cumulés pour les exercices.`);
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la progression de l'exercice:", error.message);
    throw error;
  }
};